// ABOUTME: The current value of something an effect must read but must not re-run for.
// ABOUTME: A ref kept fresh by its own effect, so nothing is ever written during render.

import { useEffect, useRef } from "react"

/**
 * Some effects legitimately read a value without being triggered by it: a snapshot to act on at the
 * moment something else changes, a guard, a callback the caller rebuilds every render. Listing such
 * a value re-runs the effect when it moves, which is exactly what the effect must not do; leaving it
 * out means an `eslint-disable` of a react-hooks rule, and an inline one of those makes the React
 * Compiler skip the whole component — a steep price for hiding one name.
 *
 * This says the same thing honestly. The ref is listable and stable, so the consuming effect's list
 * stays complete and its triggers stay exactly what the author chose.
 *
 * Call it above the effect that reads it: effects run in the order they are declared, so the update
 * below lands before the consumer runs in the same commit. The initial value is in place from the
 * first render, so the first run is correct without waiting for an effect.
 *
 * Not a way to avoid thinking about dependencies. Reach for it only when re-running would be wrong;
 * when it would merely be extra work, list the value.
 */
export function useLatest<T>(value: T): { readonly current: T } {
  const ref = useRef(value)

  useEffect(() => {
    ref.current = value
  }, [value])

  return ref
}
